import { Check, CircleDashed, ListChecks, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ListStage, LIST_UNIT_LABELS, WS_EVENT, type ListUnit } from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { cn, formatCOP } from '@/lib/cn';
import { useEventoSocket } from '@/lib/socket';
import {
  useAprobarLista,
  useDecidirSugerencia,
  useListaDelPedido,
  type PedidoDetalle,
} from '@/lib/queries';
import { Boton } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

/**
 * La lista del pedido, en vivo.
 *
 * Aquí el cliente ve al comercio trabajar: cada renglón pasa de «revisando…»
 * a disponible con precio, o a no hay —con el reemplazo sugerido para
 * aceptar o rechazar—. Abajo, el total se arma solo. Cuando el comercio
 * termina, el botón grande confirma el total y solo entonces empieza la
 * preparación: nadie recibe una cuenta que no vio.
 */
export function ListaPedido({ pedido }: { pedido: PedidoDetalle }) {
  const avisos = useAvisos();
  const cliente = useQueryClient();
  const lista = useListaDelPedido(pedido.id, !!pedido.listStage);
  const decidir = useDecidirSugerencia(pedido.id);
  const aprobar = useAprobarLista(pedido.id);

  useEventoSocket<{ orderId: string }>(WS_EVENT.LIST_UPDATED, (evento) => {
    if (evento.orderId !== pedido.id) return;
    void cliente.invalidateQueries({ queryKey: ['pedido-lista', pedido.id] });
    void cliente.invalidateQueries({ queryKey: ['pedido', pedido.id] });
  });

  if (!pedido.listStage || !lista.data) return null;
  const l = lista.data;

  return (
    <section className="card-kick mt-4 p-5">
      <h2 className="flex items-center gap-2 text-lg text-kick-dark">
        <ListChecks className="h-4 w-4 text-kick-red" />
        Tu lista
      </h2>

      {l.stage === ListStage.QUOTING && (
        <p className="mt-1 rounded-kick bg-amber-50 px-3 py-2 text-sm text-amber-800">
          El comercio está revisando tu lista renglón por renglón. Lo ves aparecer aquí en vivo.
          {l.pendientes > 0 && ` Faltan ${l.pendientes} por revisar.`}
        </p>
      )}
      {l.stage === ListStage.AWAITING_APPROVAL && (
        <p className="mt-1 rounded-kick bg-blue-50 px-3 py-2 text-sm text-blue-800">
          Lista cotizada. {l.sinDecidir > 0 ? 'Decide los reemplazos y confirma el total.' : 'Revisa y confirma el total para que empiecen a alistarla.'}
        </p>
      )}
      {l.stage === ListStage.APPROVED && (
        <p className="mt-1 rounded-kick bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Confirmaste {formatCOP(l.total)}. El comercio está alistando tu pedido.
        </p>
      )}

      <ul className="mt-3 divide-y divide-kick-gray/10">
        {l.items.map((item) => {
          const reemplazoAceptado =
            item.status === 'UNAVAILABLE' && item.suggestionDecision === 'ACCEPTED';
          return (
            <li key={item.id} className="py-2.5 text-sm">
              <div className="flex items-start gap-2">
                {item.status === 'PENDING' && (
                  <CircleDashed className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-kick-gray" />
                )}
                {item.status === 'CONFIRMED' && (
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                )}
                {item.status === 'UNAVAILABLE' && !reemplazoAceptado && (
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-kick-red" />
                )}
                {reemplazoAceptado && (
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                )}

                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block',
                      item.status === 'UNAVAILABLE' && !item.suggestionName
                        ? 'text-kick-gray line-through'
                        : 'text-kick-ink',
                    )}
                  >
                    {item.quantity} {LIST_UNIT_LABELS[item.unit as ListUnit] ?? ''} · {item.name}
                  </span>
                  {item.note && (
                    <span className="block text-xs italic text-kick-gray">«{item.note}»</span>
                  )}

                  {item.status === 'UNAVAILABLE' && item.suggestionName && (
                    <span className="mt-1 block rounded-kick bg-kick-light p-2">
                      <span className="block text-xs text-kick-gray">
                        No hay. Te sugieren:{' '}
                        <strong className="text-kick-ink">{item.suggestionName}</strong> por{' '}
                        {formatCOP(item.suggestionPrice ?? 0)}
                      </span>
                      {item.suggestionDecision === null && l.stage === ListStage.AWAITING_APPROVAL && (
                        <span className="mt-1.5 flex gap-2">
                          <Boton
                            className="px-2.5 py-1 text-xs"
                            cargando={decidir.isPending}
                            onClick={() =>
                              decidir.mutate(
                                { itemId: item.id, accept: true },
                                { onError: (e) => avisos.error(getErrorMessage(e)) },
                              )
                            }
                          >
                            Aceptar reemplazo
                          </Boton>
                          <Boton
                            variante="secundario"
                            className="px-2.5 py-1 text-xs"
                            cargando={decidir.isPending}
                            onClick={() =>
                              decidir.mutate(
                                { itemId: item.id, accept: false },
                                { onError: (e) => avisos.error(getErrorMessage(e)) },
                              )
                            }
                          >
                            No, quítalo
                          </Boton>
                        </span>
                      )}
                      {item.suggestionDecision === 'ACCEPTED' && (
                        <span className="block text-xs font-semibold text-emerald-700">
                          Aceptaste el reemplazo
                        </span>
                      )}
                      {item.suggestionDecision === 'REJECTED' && (
                        <span className="block text-xs text-kick-gray">Lo rechazaste: no va</span>
                      )}
                    </span>
                  )}
                  {item.status === 'UNAVAILABLE' && !item.suggestionName && (
                    <span className="block text-xs text-kick-red">No hay: no va en el pedido</span>
                  )}
                </span>

                <span className="shrink-0 font-semibold text-kick-ink">
                  {item.status === 'CONFIRMED' && formatCOP(item.price ?? 0)}
                  {reemplazoAceptado && formatCOP(item.suggestionPrice ?? 0)}
                  {item.status === 'PENDING' && (
                    <span className="text-xs font-normal text-kick-gray">revisando…</span>
                  )}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      <dl className="mt-3 space-y-1.5 border-t border-kick-gray/15 pt-3 text-sm">
        <div className="flex justify-between">
          <dt className="text-kick-gray">Productos</dt>
          <dd className="text-kick-ink">{formatCOP(l.subtotal)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-kick-gray">Domicilio</dt>
          <dd className="text-kick-ink">{formatCOP(l.deliveryFee)}</dd>
        </div>
        {l.serviceFee > 0 && (
          <div className="flex justify-between">
            <dt className="text-kick-gray">Tarifa lluvia</dt>
            <dd className="text-kick-ink">{formatCOP(l.serviceFee)}</dd>
          </div>
        )}
        {!!l.budgetCap && (
          <div className="flex justify-between">
            <dt className="text-kick-gray">Tu tope</dt>
            <dd className={cn(l.subtotal > l.budgetCap ? 'font-semibold text-kick-red' : 'text-kick-ink')}>
              {formatCOP(l.budgetCap)}
              {l.subtotal > l.budgetCap && ' (superado)'}
            </dd>
          </div>
        )}
      </dl>
      <div className="mt-2 flex items-baseline justify-between border-t border-kick-gray/15 pt-2">
        <span className="font-semibold text-kick-ink">Total</span>
        <span className="text-2xl font-bold text-kick-dark">{formatCOP(l.total)}</span>
      </div>

      {l.stage === ListStage.AWAITING_APPROVAL && (
        <Boton
          className="mt-4 w-full py-3 text-base"
          cargando={aprobar.isPending}
          disabled={l.sinDecidir > 0}
          onClick={() =>
            aprobar.mutate(undefined, {
              onSuccess: () => avisos.exito('Total confirmado. El comercio empieza a alistar tu pedido.'),
              onError: (e) => avisos.error(getErrorMessage(e)),
            })
          }
        >
          {l.sinDecidir > 0
            ? `Decide ${l.sinDecidir} reemplazo${l.sinDecidir === 1 ? '' : 's'} para confirmar`
            : `Confirmar total ${formatCOP(l.total)}`}
        </Boton>
      )}
    </section>
  );
}
